import { useState, useEffect, FC } from 'react';
import InputWithFloatingLabel from '../../../components/InputWithFloatingLabel';
import VendorImg from '../../../images/member-grid-placeholder.jpg';
import { Button, Grid, TextField } from '@material-ui/core';

interface AddVendorFormProps {
  handleClickCancel: () => void;
  onChangeVendorName: (value: string) => void;
  onChangeVendorDescription: (value: string) => void;
  onChangeVendorUrl: (value: string) => void;
  onChangeVendorAvatarImage: (file: File) => void;
  onAddVendor: () => void;
  onEditVendor: () => void;
  vendorForm: any;
  isEdit: boolean;
  isLoading: boolean;
}

export const AddVendorForm: FC<AddVendorFormProps> = ({
  handleClickCancel,
  onChangeVendorName,
  onChangeVendorDescription,
  onChangeVendorUrl,
  onChangeVendorAvatarImage,
  onAddVendor,
  onEditVendor,
  vendorForm,
  isEdit,
  isLoading,
}) => {
  const {
    isFormUpdated,
    id,
    name,
    description,
    vendor_url,
    avatarPath,
    errorName,
    errorDescription,
  } = vendorForm;

  // declare
  const isAddMode = !id;
  const disableFlag = !isFormUpdated || isLoading;
  const [selectedImg, setSelectedImg] = useState(
    avatarPath ? `/${avatarPath}` : VendorImg,
  );
  const [selectedUser, setSelectedUser] = useState<File | null>(null);

  const onImageUpload = (file?: any) => {
    if (!file) return;
    setSelectedUser(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setSelectedImg(reader.result);
      }
    };
  };

  useEffect(() => {
    if (selectedUser) {
      onChangeVendorAvatarImage(selectedUser);
    }
  }, [selectedUser]);

  // Update selectedImg when avatarPath changes (for edit mode)
  useEffect(() => {
    if (avatarPath) {
      setSelectedImg(avatarPath.startsWith('http') ? avatarPath : `/${avatarPath}`);
    } else if (!selectedUser) {
      setSelectedImg(VendorImg);
    }
  }, [avatarPath, selectedUser]);

  return (
    <div className="container add-vendor-content br-form-element">
      <Grid container spacing={6}>
        {/* Left side - Name and Description */}
        <Grid item xs={12} sm={7} md={8}>
          <Grid className="mt-24">
            <InputWithFloatingLabel
              labelName="Name"
              inputValue={name}
              handleOnChange={onChangeVendorName}
              errorStatus={errorName}
              errorMessage={'Please enter vendor name'}
              required={true}
              isLoading={isLoading}
            />
          </Grid>
          
          <Grid className="mt-24">
            <TextField
              label="Description"
              variant="outlined"
              className="text-box"
              multiline
              rows={4}
              value={description}
              onChange={(e) => onChangeVendorDescription(e.target.value)}
              disabled={isLoading}
              error={errorDescription}
              helperText={errorDescription ? 'Please enter description' : ''}
            />
          </Grid>

          <Grid className="mt-24">
            <InputWithFloatingLabel
              labelName="Vendor Website URL"
              inputValue={vendor_url}
              handleOnChange={onChangeVendorUrl}
              errorStatus={false}
              errorMessage={''}
              required={false}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        {/* Right side - Image and Upload button */}
        <Grid item xs={12} sm={5} md={4}>
          <div className="image-add-field">
            <img
              alt="vendorImage"
              src={isAddMode ? selectedImg : (selectedImg !== VendorImg ? selectedImg : (avatarPath ? `/${avatarPath}` : VendorImg))}
              onError={() => {
                setSelectedImg(VendorImg);
              }}
            />
          </div>
          <div className="upload-img">
            <Button className="img-filed" component="label">
              <i className="far fa-image image-field-icon"></i>
              {isAddMode ? 'Add Image' : 'Update Image'}
              <input
                type="file"
                onChange={(e) => onImageUpload(e?.target?.files?.[0])}
                hidden
                disabled={isLoading}
              />
            </Button>
          </div>
        </Grid>
      </Grid>
      
      <Grid className="button-container">
        <Button
          type="button"
          className="button-cancel"
          onClick={handleClickCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className={disableFlag ? 'button-save button-disable' : 'button-save'}
          onClick={() =>
            isEdit ? onEditVendor() : onAddVendor()
          }
          disabled={disableFlag}
        >
          {isAddMode ? 'Save' : 'Update'}
        </Button>
      </Grid>
    </div>
  );
};
